import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  SimpleGrid,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { ArrowLeft, CaretLeft, Notepad } from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";
import { WithAuthorization } from "../../context";
import { useRightSidebar } from "../../context/RightSidebarContext";
import { routes } from "../../config/paths";
import { buildProductRows, buildSupplierDivisions, COMPANY_WIDE, getSupplierTask } from "./suppliersMock";
import TaskDataTypePanel from "./components/detail/TaskDataTypePanel";
import TaskManagePanel from "./components/detail/TaskManagePanel";
import SupplierTaskCard from "./components/detail/SupplierTaskCard";
import SourcesCard from "./components/detail/SourcesCard";

const TASK_TRAY_ID = "TaskDetails";

// Per-task data-entry page, foundation-data style: the active task is the main
// editor; the other data tasks for the same reporting period sit below as
// collapsible cards. Task Details opens in a right-hand tray.
function SupplierTaskDetail() {
  const navigate = useNavigate();
  const toast = useToast();
  const { setRightSidebar, doOpen, doClose, isOpen, activationId } = useRightSidebar();
  const { supplierId, taskId } = useParams();

  const found = useMemo(() => getSupplierTask(supplierId, taskId), [supplierId, taskId]);

  // Field answers + uploaded files keyed by task id. Local mock state.
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  // Product-table rows keyed by task id (seeded for Product tasks).
  const [productRows, setProductRows] = useState({});
  // Scope emissions tables keyed by task id → scope tab id → category → division.
  const [emissions, setEmissions] = useState({});

  if (!found) {
    return (
      <Box p={4} mx={{ base: 4, md: "10%", xl: "15%" }}>
        <Button leftIcon={<ArrowLeft size={18} />} variant="plain" colorScheme="gray" onClick={() => navigate(routes.supplyChainSupplierData)} mb={4}>
          Tasks
        </Button>
        <Text color="gray.500">Task not found.</Text>
      </Box>
    );
  }

  const { supplier, task, siblings } = found;
  const backToSupplier = () =>
    navigate(routes.supplyChainSupplierDetail.replace(":supplierId", supplier.id));
  const openTask = (id) =>
    navigate(
      routes.supplyChainSupplierTask
        .replace(":supplierId", supplier.id)
        .replace(":taskId", id)
    );

  const toggleTaskDetails = () => {
    if (TASK_TRAY_ID === activationId && isOpen) {
      doClose();
    } else {
      setRightSidebar(<TaskManagePanel supplier={supplier} task={task} onEdit={doClose} />);
      doOpen(TASK_TRAY_ID);
    }
  };

  const setFieldValue = (id, key, value) =>
    setAnswers((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: value } }));
  const setTaskFiles = (id, next) => setFiles((prev) => ({ ...prev, [id]: next }));

  const divisions = buildSupplierDivisions(supplier);
  // Update one emissions cell: task → scope tab → category → division.
  const setEmissionCell = (taskId, scopeId, category, division, value) =>
    setEmissions((prev) => {
      const t = prev[taskId] || {};
      const scope = t[scopeId] || {};
      const cat = scope[category] || {};
      return {
        ...prev,
        [taskId]: { ...t, [scopeId]: { ...scope, [category]: { ...cat, [division]: value } } },
      };
    });

  const handleSave = () => {
    toast({ title: "Draft saved", status: "success", duration: 2500, isClosable: true });
    backToSupplier();
  };
  const handleFinalise = () => {
    toast({ title: "Task finalised", status: "success", duration: 2500, isClosable: true });
    backToSupplier();
  };

  const periodLabel = `${task.year}${task.quarter !== "ALL" ? ` · ${task.quarter}` : ""}`;
  const title = `${task.dataType} ${task.year}`;

  return (
    <Flex direction="column" w="100%" minH="100vh">
      {/* Secondary header bar — matches the carbon data-task page: a Task
          Details tray toggle pinned to the right. */}
      <Flex position="sticky" top={0} zIndex={10} bg="white" h="52px" flexShrink={0} align="center" justify="flex-end">
        {!(activationId === TASK_TRAY_ID && isOpen) && (
          <Box borderLeft="1px solid" borderBottom="1px solid" borderColor="gray.200" h="100%" display="flex" alignItems="center" px={4} bg="white">
            <Button
              variant="ghost"
              onClick={toggleTaskDetails}
              fontSize="14px"
              _hover={{ bg: "transparent" }}
              _active={{ bg: "transparent" }}
              leftIcon={<CaretLeft size={16} />}
              rightIcon={<Notepad size={16} color="var(--chakra-colors-limeShade-600)" />}
            >
              Task Details
            </Button>
          </Box>
        )}
      </Flex>

      <Box p={4} mx={{ base: 4, md: "8%", xl: "12%" }} bg="pureWhite">
        <Button leftIcon={<ArrowLeft size={18} />} variant="plain" colorScheme="gray" onClick={backToSupplier} pl={0} mb={4}>
          <Text fontSize="body.l" fontWeight="600" color="gray.700">{supplier.name}</Text>
        </Button>

        <Box mb={6}>
          <HStack spacing={3} mb={1} align="center">
            <Text fontSize="heading.2xl" fontWeight="700" color="darkTeal">{title}</Text>
            <Text fontSize="13px" color="gray.400">· {task.division || COMPANY_WIDE}</Text>
          </HStack>
          <Text fontSize="body.m" color="gray.500">Supplier data task · {periodLabel}</Text>
        </Box>

        {/* Active task editor */}
        <TaskDataTypePanel
          dataType={task.dataType}
          values={answers[task.id] || {}}
          onFieldChange={(key, value) => setFieldValue(task.id, key, value)}
          productRows={productRows[task.id] || (task.dataType === "Product" ? buildProductRows(supplier) : [])}
          onProductRowsChange={(next) => setProductRows((prev) => ({ ...prev, [task.id]: next }))}
          divisions={divisions}
          emissions={emissions[task.id] || {}}
          onEmissionsChange={(scopeId, category, division, value) => setEmissionCell(task.id, scopeId, category, division, value)}
        />

        {/* Sources / supporting documents */}
        <Box mt={10}>
          <SourcesCard files={files[task.id] || []} onChange={(next) => setTaskFiles(task.id, next)} />
        </Box>

        {/* Other data tasks for the same period — reuse the standard task
            cards; clicking opens that task's own detail page. */}
        {siblings.length > 0 && (
          <Box mt={12}>
            <Text fontSize="body.m" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" mb={3}>
              Other data for {task.year}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
              {siblings.map((s) => (
                <SupplierTaskCard key={s.id} task={s} onOpen={() => openTask(s.id)} />
              ))}
            </SimpleGrid>
          </Box>
        )}
      </Box>

      {/* Sticky footer */}
      <Flex
        direction="row"
        justify="space-between"
        gap={4}
        mt={8}
        position="sticky"
        bottom="0"
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        py={4}
        px={{ base: 4, md: "8%", xl: "12%" }}
        zIndex="10"
      >
        <Button flex={1} bg="limeShade.100" color="pureBlack" _hover={{ bg: "limeShade.200" }} onClick={handleSave}>
          Save &amp; close
        </Button>
        <Tooltip label="" placement="top">
          <Button flex={1} bg="lime" color="pureBlack" _hover={{ bg: "limeShade.300" }} onClick={handleFinalise}>
            Finalise
          </Button>
        </Tooltip>
      </Flex>
    </Flex>
  );
}

export default WithAuthorization(SupplierTaskDetail);
